import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    FileText,
    Briefcase,
    MessageSquare,
    Settings,
    LogOut,
    X,
    Moon,
    Sun,
    Monitor
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeContext';

interface AdminSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    // Get user role from localStorage
    const userRole = useMemo(() => {
        try {
            const adminUser = localStorage.getItem('adminUser');
            if (adminUser) {
                const user = JSON.parse(adminUser);
                return user.role || 'admin';
            }
        } catch (e) {
            console.error('Error parsing admin user:', e);
        }
        return 'admin';
    }, []);

    const isSuperAdmin = userRole === 'super_admin';

    const handleLogout = () => {
        localStorage.removeItem('adminAuth');
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminUser');
        toast.success('Logged out successfully');
        navigate('/login');
    };

    const handleNavClick = () => {
        // Close sidebar on mobile after navigation
        onClose();
    };

    // Filter nav items based on role - Users only visible to super_admin
    const navItems = useMemo(() => {
        const items = [
            { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'super_admin'] },
            { path: '/admin/enquiries', icon: MessageSquare, label: 'Enquiries', roles: ['admin', 'super_admin'] },
            { path: '/admin/applications', icon: Briefcase, label: 'Applications', roles: ['admin', 'super_admin'] },
            { path: '/admin/resumes', icon: FileText, label: 'Resumes', roles: ['admin', 'super_admin'] },
            { path: '/admin/careers', icon: Briefcase, label: 'Careers', roles: ['admin', 'super_admin'] },
            { path: '/admin/users', icon: Users, label: 'Users', roles: ['super_admin'] }, // Super Admin only
            { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['admin', 'super_admin'] }
        ];
        
        return items.filter(item => item.roles.includes(userRole));
    }, [userRole]);

    return (
        <>
            {/* Mobile Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <aside className={`
                fixed left-0 top-0 h-full w-64 glass-strong border-r border-primary/30 z-50
                transition-transform duration-300 ease-in-out
                lg:translate-x-0
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Logo Section */}
                    <div className="p-6 border-b border-primary/30">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <img 
                                    src='/logo.png' 
                                    alt="amasQIS Logo" 
                                    className="w-10 h-10"
                                />
                                <div>
                                    <h3 
                                        onClick={() => navigate('/')}
                                        className="text-foreground cursor-pointer hover:text-primary transition-colors duration-300"
                                        style={{
                                            fontFamily: "'Comfortaa', sans-serif"
                                        }}
                                    >
                                        amasQIS.ai
                                    </h3>
                                    <p className="text-xs text-muted-foreground">Admin Dashboard</p>
                                    <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                        isSuperAdmin 
                                            ? 'bg-primary/20 text-primary border border-primary/30' 
                                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    }`}>
                                        {isSuperAdmin ? 'Super Admin' : 'Admin'}
                                    </span>
                                </div>
                            </div>
                            {/* Close button for mobile */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="lg:hidden text-muted-foreground hover:text-foreground"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;
                            
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={handleNavClick}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                        isActive
                                            ? 'bg-primary/10 text-primary border border-primary/20'
                                            : 'text-muted-foreground hover:bg-primary/5 hover:text-foreground'
                                    }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Theme Toggle & Logout */}
                    <div className="p-4 border-t border-primary/30 space-y-2">
                        <Button
                            onClick={toggleTheme}
                            variant="ghost"
                            className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-primary/5"
                            title={`Current: ${theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'System'} Mode (Click to cycle)`}
                        >
                            {theme === 'dark' ? (
                                <>
                                    <Moon className="w-5 h-5 mr-3" />
                                    <span>Dark Mode</span>
                                </>
                            ) : theme === 'light' ? (
                                <>
                                    <Sun className="w-5 h-5 mr-3" />
                                    <span>Light Mode</span>
                                </>
                            ) : (
                                <>
                                    <Monitor className="w-5 h-5 mr-3" />
                                    <span>System</span>
                                </>
                            )}
                        </Button>
                        <Button
                            onClick={handleLogout}
                            variant="ghost"
                            className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-primary/5"
                        >
                            <LogOut className="w-5 h-5 mr-3" />
                            <span>Logout</span>
                        </Button>
                    </div>
                </div>
            </aside>
        </>
    );
}